import { useEffect, useState } from "react";
import { Download, FileText, Mail, RefreshCw, Save, PlugZap } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { openPdf } from "./OrderDocuments";

const inputCls = "w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-sm text-white outline-none";
const thisMonth = () => new Date().toISOString().slice(0, 7);
const eur = (v) => `€ ${((v || 0) + 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const err = (e) => toast.error(e?.response?.data?.detail || "Errore");

const FIC_LABEL = {
  pending: ["In coda", "text-[#9CA3AF]"],
  "not-configured": ["Da collegare", "text-[#C9A227]"],
  synced: ["Registrato", "text-emerald-400"],
  "sent-sdi": ["Inviata SdI", "text-emerald-400"],
  error: ["Errore", "text-[#DC2626]"],
  manual: ["Storno da registrare", "text-[#C9A227]"],
  "n/a": ["—", "text-[#6B7280]"],
};
const MAIL_LABEL = { pending: "In invio", sent: "Inviata", error: "Errore", "no-email": "Nessuna email", "n/a": "—" };

const SField = ({ label, k, s, setS, type = "text", testid, placeholder }) => (
  <label className="block">
    <span className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    <input type={type} data-testid={testid} placeholder={placeholder} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={`${inputCls} mt-1`} />
  </label>
);

const SettingsForm = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/invoicing/settings").then(({ data }) => setS({ ...data, fic_access_token: "" })); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/invoicing/settings", { ...s, fic_vat_id: parseInt(s.fic_vat_id || 0, 10) });
      setS({ ...data, fic_access_token: "" });
      toast.success("Impostazioni salvate");
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const test = async () => {
    try {
      const { data } = await api.post("/admin/invoicing/fic-test");
      toast.success(`Collegato: ${data.companies.map((c) => `${c.name} (${c.id})`).join(", ") || "nessuna azienda"}`);
      const { data: fresh } = await api.get("/admin/invoicing/settings");
      setS({ ...fresh, fic_access_token: "" });
    } catch (e) { err(e); }
  };
  return (
    <div className="border border-[#C9A227]/20 rounded-sm p-5 bg-[#0b0b0d] space-y-5">
      <div className="text-xs font-mono-ps uppercase tracking-widest text-[#C9A227]">Intestazione ricevute</div>
      <div className="grid md:grid-cols-3 gap-3">
        <SField label="Ragione sociale / Nome" k="issuer_name" s={s} setS={setS} testid="inv-set-name" />
        <SField label="Indirizzo sede" k="issuer_address" s={s} setS={setS} testid="inv-set-address" />
        <SField label="Partita IVA" k="issuer_vat" s={s} setS={setS} testid="inv-set-vat" />
        <SField label="Codice fiscale" k="issuer_tax_code" s={s} setS={setS} testid="inv-set-cf" />
        <SField label="Email" k="issuer_email" s={s} setS={setS} testid="inv-set-email" />
        <SField label="Telefono" k="issuer_phone" s={s} setS={setS} testid="inv-set-phone" />
      </div>
      <div className="text-xs font-mono-ps uppercase tracking-widest text-[#C9A227] pt-2">Fatture in Cloud</div>
      <div className="grid md:grid-cols-3 gap-3">
        <SField label={`Access Token ${s.fic_token_set ? `(salvato ${s.fic_token_hint})` : "(non inserito)"}`} k="fic_access_token" s={s} setS={setS} type="password" testid="inv-set-fic-token" placeholder={s.fic_token_set ? "Lascia vuoto per non cambiarlo" : "Incolla il token"} />
        <SField label="ID azienda" k="fic_company_id" s={s} setS={setS} testid="inv-set-fic-company" placeholder="Si compila da solo con 'Prova collegamento'" />
        <SField label="ID aliquota IVA 22%" k="fic_vat_id" s={s} setS={setS} type="number" testid="inv-set-fic-vat" />
      </div>
      <label className="flex items-center gap-3 text-sm text-white">
        <input type="checkbox" data-testid="inv-set-fic-sdi" checked={!!s.fic_send_sdi} onChange={(e) => setS({ ...s, fic_send_sdi: e.target.checked })} className="accent-[#C9A227] w-4 h-4" />
        Invia automaticamente allo SdI le fatture richieste dai clienti
      </label>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} data-testid="inv-set-save" className="btn-gold disabled:opacity-60"><Save size={14} /> Salva</button>
        <button onClick={test} data-testid="inv-set-fic-test" className="btn-ghost-gold"><PlugZap size={14} /> Prova collegamento</button>
      </div>
    </div>
  );
};

const DocRow = ({ d, refresh }) => {
  const act = async (path, check) => {
    try {
      const { data } = await api.post(`/admin/documents/${d.doc_id}/${path}`);
      const msg = check(data);
      msg.ok ? toast.success(msg.text) : toast.error(msg.text);
      refresh();
    } catch (e) { err(e); }
  };
  const resend = () => act("resend-email", (x) => (x.email_status === "sent" ? { ok: true, text: "Email inviata" } : { ok: false, text: x.email_error || "Email non inviata" }));
  const sync = () => act("fic-sync", (x) => (["synced", "sent-sdi"].includes(x.fic_status) ? { ok: true, text: "Registrato su Fatture in Cloud" }
    : { ok: false, text: x.fic_status === "not-configured" ? "Fatture in Cloud non ancora collegato" : x.fic_error || "Sincronizzazione non riuscita" }));
  const [fic, ficCls] = FIC_LABEL[d.fic_status] || [d.fic_status, "text-white"];
  const isRefund = d.kind === "refund";
  const ref = d.kind === "voucher" ? `Buono ${d.voucher_code}` : `${isRefund ? "Storno ordine" : "Ordine"} #${d.order_id?.slice(-8).toUpperCase()}`;
  return (
    <tr data-testid={`doc-row-${d.number}`} className="border-b border-[#C9A227]/8 hover:bg-[#C9A227]/5">
      <td className="px-3 py-3 font-mono-ps text-[#C9A227] whitespace-nowrap">{d.number}</td>
      <td className="px-3 py-3 text-[#9CA3AF] whitespace-nowrap">{d.date.split("-").reverse().join("/")}</td>
      <td className="px-3 py-3 text-white/85">
        {d.customer?.name || d.customer?.email}
        <div className="text-[11px] text-[#6B7280]">{ref}{d.invoice_request ? " • fattura" : ""}</div>
      </td>
      <td className={`px-3 py-3 font-mono-ps font-bold whitespace-nowrap ${isRefund ? "text-[#DC2626]" : "text-white"}`}>{eur(d.total)}</td>
      <td className="px-3 py-3 text-xs max-w-[180px]">
        {MAIL_LABEL[d.email_status] || d.email_status}
        {d.email_status === "error" && d.email_error && <div data-testid={`doc-email-error-${d.number}`} className="text-[10px] text-[#DC2626] mt-0.5 line-clamp-3">{d.email_error}</div>}
      </td>
      <td className={`px-3 py-3 text-xs max-w-[200px] ${ficCls}`}>
        {fic}
        {d.fic_status === "error" && d.fic_error && <div data-testid={`doc-fic-error-${d.number}`} className="text-[10px] text-[#DC2626] mt-0.5 line-clamp-3">{d.fic_error}</div>}
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-1">
          {!isRefund && (
            <>
              <button onClick={() => openPdf(d.doc_id)} data-testid={`doc-pdf-${d.number}`} title="Apri PDF" className="p-1.5 text-[#C9A227] hover:text-white"><FileText size={14} /></button>
              <button onClick={resend} data-testid={`doc-resend-${d.number}`} title="Reinvia email" className="p-1.5 text-[#C9A227] hover:text-white"><Mail size={14} /></button>
            </>
          )}
          {d.kind !== "voucher" && !["synced", "sent-sdi"].includes(d.fic_status) && (
            <button onClick={sync} data-testid={`doc-sync-${d.number}`} title="Invia a Fatture in Cloud" className="p-1.5 text-[#C9A227] hover:text-white"><RefreshCw size={14} /></button>
          )}
        </div>
      </td>
    </tr>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-4 bg-[#0b0b0d]">
    <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</div>
    <div data-testid={testid} className="font-mono-ps text-lg text-white font-bold mt-1">{value}</div>
  </div>
);

export const DocumentsPanel = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState({ documents: [], totals: {} });
  const load = () => api.get("/admin/documents", { params: { month } }).then(({ data }) => setData(data)).catch(err);
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const { data: blob } = await api.get("/admin/documents/export", { params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pullstation-contabilita-${month}.csv`;
      a.click();
    } catch (e) { err(e); }
  };
  const syncAll = async () => {
    try { const { data: r } = await api.post("/admin/documents/fic-sync-pending"); toast.success(`${r.processed} documenti processati`); load(); } catch (e) { err(e); }
  };
  const t = data.totals;

  return (
    <div className="mt-6 space-y-6">
      <SettingsForm />
      <div className="flex flex-wrap items-end gap-3">
        <label>
          <span className="block text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">Mese</span>
          <input type="month" data-testid="docs-month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} mt-1`} />
        </label>
        <button onClick={exportCsv} data-testid="docs-export" className="btn-gold"><Download size={14} /> Export per commercialista</button>
        <button onClick={syncAll} data-testid="docs-sync-all" className="btn-ghost-gold"><RefreshCw size={14} /> Invia in coda a Fatture in Cloud</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        <Stat label="Documenti" value={t.count || 0} testid="docs-total-count" />
        <Stat label="Incassato (lordo)" value={eur(t.gross)} testid="docs-total-gross" />
        <Stat label="Imponibile" value={eur(t.net)} testid="docs-total-net" />
        <Stat label="IVA 22%" value={eur(t.vat)} testid="docs-total-vat" />
        <Stat label="Da bonifico" value={eur(t.paid_bank)} testid="docs-total-bank" />
        <Stat label="Pagato con buoni" value={eur(t.paid_voucher)} testid="docs-total-voucher-used" />
        <Stat label="Buoni emessi" value={eur(t.vouchers_issued)} testid="docs-total-voucher-issued" />
      </div>
      <div className="border border-[#C9A227]/15 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0b0b0d] border-b border-[#C9A227]/15">
            <tr className="text-left">
              {["Numero", "Data", "Cliente", "Totale", "Email", "Fatture in Cloud", ""].map((h) => (
                <th key={h} className="px-3 py-3 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.documents.map((d) => <DocRow key={d.doc_id} d={d} refresh={load} />)}
            {data.documents.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#9CA3AF]">Nessun documento in questo mese. Le ricevute si creano quando segni un ordine come pagato.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
